// src/components/ImageUploader.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../utils/supabaseUtils';
import { logUserUpload } from '../utils/supabaseUtils';
import { addWatermark } from '../utils/imageUtils';
import { removeBackground } from '../services/removeBgService';
import { safeLog, errorLog, devLog } from '../utils/loggerUtils';

const ImageUploader = ({ onUploadComplete }) => {
  const [uploadedImageURL, setUploadedImageURL] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSession = async () => {
      const { data, error } = await supabase.auth.getSession();
      devLog('ImageUploader session check:', data);
      if (error || !data.session) {
        devLog('No session found, redirecting to login');
        navigate('/login');
      } else {
        setUser(data.session.user);
      }
      setCheckingSession(false);
    };
    fetchSession();

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      devLog('Auth event in ImageUploader:', { event, hasSession: !!session });
      if (event === 'SIGNED_IN' && session) {
        setUser(session.user);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        navigate('/login');
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [navigate]);

  const handleFileChange = async (event) => {
    if (!user) {
      setError('Please log in to upload an image');
      return;
    }

    setIsLoading(true);
    setError(null);
    const file = event.target.files[0];
    if (!file) {
      setIsLoading(false);
      return;
    }

    try {
      devLog('Processing image for background removal:', file.name);
      devLog('Image size:', Math.round(file.size / 1024), 'KB');
      devLog('Image type:', file.type);

      // Use our enhanced client-side removeBackground function
      // This already handles trimming and has server fallback
      const processedBlob = await removeBackground(file);
      safeLog('Processed image size:', Math.round(processedBlob.size / 1024) + ' KB');
      
      const processedFile = new File([processedBlob], `processed-hq-${Date.now()}.png`, { type: 'image/png' });

      const filePath = `background_removed_images/${user.id}/${processedFile.name}`;
      const { data, error: uploadError } = await supabase.storage
        .from('playmakerpics-images')
        .upload(filePath, processedFile, { upsert: true });

      if (uploadError) {
        devLog('Storage upload error:', uploadError);
        throw uploadError;
      }

      const { publicUrl } = supabase.storage
        .from('playmakerpics-images')
        .getPublicUrl(filePath).data;

      safeLog('Public URL:', publicUrl);
      
      // Create watermarked version of the image
      devLog('Creating watermarked version of the image...');
      const watermarkedBlob = await addWatermark(processedBlob, {
        text: "PlayMaker Pics",
        color: "rgba(255, 255, 255, 0.8)",
        font: "bold 36px Arial",
        repeating: true,
        diagonal: true,
      });
      
      // Create a file for the watermarked version
      const watermarkedFile = new File(
        [watermarkedBlob],
        `processed-hq-watermarked-${Date.now()}.png`,
        { type: 'image/png' }
      );
      
      // Upload watermarked version to Supabase storage
      const watermarkedFilePath = `background_removed_images/${user.id}/watermarked/${watermarkedFile.name}`;
      const { data: wmData, error: wmUploadError } = await supabase.storage
        .from('playmakerpics-images')
        .upload(watermarkedFilePath, watermarkedFile, { upsert: true });
      
      let watermarkedPublicUrl = null;
      
      if (wmUploadError) {
        devLog('Watermarked image upload error:', wmUploadError);
      } else {
        const { publicUrl: wmPublicUrl } = supabase.storage
          .from('playmakerpics-images')
          .getPublicUrl(watermarkedFilePath).data;
          
        watermarkedPublicUrl = wmPublicUrl;
        devLog('Watermarked Public URL:', watermarkedPublicUrl);
      }
      
      // Log the upload in the database, including watermarked URL
      await logUserUpload(user.id, publicUrl, watermarkedPublicUrl);

      setUploadedImageURL(publicUrl);
      onUploadComplete(publicUrl, watermarkedPublicUrl);
    } catch (err) {
      errorLog('Upload error:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (checkingSession) {
    return <div>Checking session...</div>;
  }

  return (
    <div>
      <input type="file" accept="image/*" onChange={handleFileChange} disabled={isLoading || !user} />
      {isLoading && <p>Processing image...</p>}
      {error && <p style={{ color: 'red' }}>{error}</p>}
      {uploadedImageURL && <img src={uploadedImageURL} alt="Processed Image" style={{ maxWidth: '300px' }} />}
    </div>
  );
};

export default ImageUploader;